import { useQuery } from "@tanstack/react-query";
import type React from "react";

import { Blockquote } from "@/components/Blockquote/Blockquote";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { userMutations } from "@/queries/users/mutations";
import { useUserQueries } from "@/queries/users/queryOptions";
import { useTranslation } from "@/translations";

type ReinviteUserModalProps = ModalBaseProps & {
  userId: string;
};

export function ReinviteUserModal({ userId, isOpened, onOpenChange }: ReinviteUserModalProps): React.ReactNode {
  const { t, i18n } = useTranslation();

  const userQueries = useUserQueries();
  const { data: signUpReminders, isLoading: isLoadingSignUpReminders } = useQuery({
    ...userQueries.signUpReminders({
      userId: userId,
    }),
    enabled: isOpened && Boolean(userId),
  });

  const addUserSignUpRemindersMutation = userMutations.useAddUserSignUpReminders();

  const handleConfirm = () => {
    addUserSignUpRemindersMutation.mutate(userId, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };
  const sentReminders = signUpReminders?.reminders || [];
  const lastSentReminder = sentReminders.at(-1);

  return (
    <ConfirmModal
      tone="primary"
      title={t("component.reinvite-user-modal.title")}
      isLoading={addUserSignUpRemindersMutation.isPending}
      isOpened={isOpened}
      onResolve={handleConfirm}
      resolveBtnProps={{
        text: t("component.reinvite-user-modal.btn.confirm"),
      }}
      onReject={() => onOpenChange(false)}
      onOpenChange={onOpenChange}
      description={t("component.reinvite-user-modal.description", {
        date: lastSentReminder ? formatDate(i18n, "date", lastSentReminder.lastReminded) : "",
      })}
    >
      <div className="relative py-space-md">
        {isLoadingSignUpReminders && (
          <div className="absolute inset-0 z-0 flex items-center justify-center bg-background-default">
            <FullSizeLoader size="sm" noDelay />
          </div>
        )}
        <Blockquote>
          {t("component.reinvite-user-modal.amount-invitations-sent", {
            count: signUpReminders?.reminders.length || "-",
          })}
        </Blockquote>
      </div>
    </ConfirmModal>
  );
}
